// Gmastr.jsx
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdEdit, MdAddCircle, MdFilterList, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from "@mui/material";
import loading from "../../../assets/images/common/loading.gif";
import AppDataGrid from "../../../components/common/AppDataGrid";

const Gmastr = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const formatDate = (datetime) => {
    if (!datetime) return "";
    const date = new Date(datetime);
    if (isNaN(date)) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${day}-${month}-${year}`;
  };

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [Groups, setGroups] = useState([]);
  const [allGroups, setAllGroups] = useState([]); // ✅ keep master copy for client-side filter
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const handleShowFilterModal = () => setShowFilterModal(true);
  const handleCloseFilterModal = () => setShowFilterModal(false);

  const [filter, setFilter] = useState({
    parent_group_name: "",
    parent_group_code: "",
    group_name: "",
    group_code: "",
    action: "FILTER",
  });

  // ✅ READ
  const fetchYears = async () => {
    try {
      setIsLoading(true);
      const res = await axios.post(`${baseUrl}/groupsfilter/`, {
        action: "READ",
        school_id: userObj.school_id,
      });
      const data = res.data;
      const records = Array.isArray(data) && !data[0]?.error ? data : [];
      setGroups(records);
      setAllGroups(records); // ✅ store master copy
    } catch (err) {
      console.error(err);
      setGroups([]);
      setAllGroups([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Edit
  const handleEditClick = (group_id) => {
    const fyToEdit = Groups.find((x) => x.group_id === group_id);
    if (fyToEdit) {
      navigate("/finance/addgmstr", { state: { groupData: fyToEdit } });
    } else {
      toast.error("Financial year not found");
    }
  };

  // ✅ Delete / Inactive
  const handleDeleteClick = async (group_id) => {
    const confirmDelete = window.confirm("Are you sure you want to change the status?");
    if (!confirmDelete) return;

    const requestBody = {
      group_id: group_id,
      user: userObj.user_name,
    };
    try {
      const response = await axios.post(baseUrl + "/groups_soft_delete/", requestBody, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      toast.success("Record Set to InActive");
      fetchYears();
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const searchableColumns = [
    (row) => row.parent_group_name,
    (row) => row.parent_group_code,
    (row) => row.group_name,
    (row) => (row.is_active ? "Active" : "Inactive"),
    (row) => row.group_code,
  ];

  const filteredRecords = (Groups || []).filter((item) =>
    searchableColumns.some((selector) => {
      const value = selector(item);
      const stringValue = String(value || "").toUpperCase().replace(/[-\s]+/g, "");
      const normalizedQuery = searchQuery.toUpperCase().replace(/[-\s]+/g, "");
      return stringValue.includes(normalizedQuery);
    })
  );

  const columns = [
    {
      field: "parent_group_name",
      headerName: "Parent Group Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "parent_group_code",
      headerName: "Parent Group Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "group_name",
      headerName: "Group Name",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "group_code",
      headerName: "Group Code",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value || ""}>
          <span>{params.value}</span>
        </Tooltip>
      ),
      sortable: true,
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const label = params.value || "";
        return (
          <Tooltip title={label}>
            <span>{label}</span>
          </Tooltip>
        );
      },
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="tableActions">
          <span className="commonActionIcons" onClick={() => handleEditClick(params.row.group_id)}>
            <MdEdit />
          </span>
          <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.group_id)}>
            <MdDelete />
          </span>
        </div>
      ),
    },
  ];

  // ✅ FIX: Filter applied client-side so parent_group_name & parent_group_code actually work
  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const pgName = filter.parent_group_name.trim().toLowerCase();
    const pgCode = filter.parent_group_code.trim().toLowerCase();
    const gName  = filter.group_name.trim().toLowerCase();
    const gCode  = filter.group_code.trim().toLowerCase();

    // If all fields empty, just show all
    if (!pgName && !pgCode && !gName && !gCode) {
      setGroups(allGroups);
      setShowFilterModal(false);
      return;
    }

    const filtered = allGroups.filter((row) => {
      const matchPgName = !pgName || (row.parent_group_name || "").toLowerCase().includes(pgName);
      const matchPgCode = !pgCode || (row.parent_group_code || "").toLowerCase().includes(pgCode);
      const matchGName  = !gName  || (row.group_name || "").toLowerCase().includes(gName);
      const matchGCode  = !gCode  || (row.group_code || "").toLowerCase().includes(gCode);
      return matchPgName && matchPgCode && matchGName && matchGCode;
    });

    // ✅ TC4: No results is not a duplicate — only show no-record message if truly empty
    if (filtered.length === 0) {
      toast.info("No matching records found");
    }

    setGroups(filtered);
    setShowFilterModal(false);
  };

  const handleReset = (e) => {
    e.preventDefault();
    setFilter({
      parent_group_name: "",
      parent_group_code: "",
      group_name: "",
      group_code: "",
      action: "FILTER",
    });
    setGroups(allGroups); // ✅ restore full list
    setShowFilterModal(true);
  };

  return (
    <div className="pageBody">
      <ToastContainer />

      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Groups</h6>
          </div>

          <div>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              className="searchInput"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>

            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate("/finance/addgmstr")}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords.length ? filteredRecords : []}
              columns={columns}
              getRowId={(row) => row.group_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>

        <Modal.Body className="modalBodyScrollable">
          <Row>
            {/* ✅ TC1 & TC2: Label and placeholder correctly say "Parent Group Name" */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="parent_group_name">
                  <Form.Label>Parent Group Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Parent Group Name"
                    value={filter.parent_group_name}
                    onChange={(e) => setFilter({ ...filter, parent_group_name: e.target.value })}
                  />
                </Form.Group>
              </div>
            </Col>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="parent_group_code">
                  <Form.Label>Parent Group Code</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Parent Group Code"
                    value={filter.parent_group_code}
                    onChange={(e) => setFilter({ ...filter, parent_group_code: e.target.value })}
                  />
                </Form.Group>
              </div>
            </Col>
            {/* ✅ TC3: Group Name field added so filter works */}
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="group_name">
                  <Form.Label>Group Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Group Name"
                    value={filter.group_name}
                    onChange={(e) => setFilter({ ...filter, group_name: e.target.value })}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Gmastr;